import { useEffect } from 'react'
import { queryClient } from '@/lib/api-client.ts'
import { useSyncQueueStore } from '@/lib/helpers/bgSync/useSyncQueueStore.ts'
import { countPendingQueueEntriesForType } from '@/lib/helpers/bgSync/pendingQueueState.ts'
import { useBulkSelectStore } from '@/store/useBulkSelectStore.ts'

// Map queue `meta.type` (= first URL path segment, set by
// `nativeQueueManager.enqueueRequest`) → React Query keys to invalidate when
// the queue fully drains for that type. Mirrors the live
// `MUTATION_INVALIDATION_MAP` in `api-client.ts`, but we cannot use that one
// directly because it runs at the axios layer where queueable endpoints are
// deliberately skipped (the mock 202 response would otherwise loop with the
// real server response).
const DRAIN_INVALIDATION_MAP: Record<string, string[]> = {
  issues: ['singleIssue', 'stageIssuesByIssueType'],
  comments: ['singleIssueComments', 'singleIssueEvents'],
  media_objects: ['singleIssue', 'singleReportIssues'],
  locations: ['stageLocations', 'getProjectStageLocations'],
  // `project_stages` is here for ONE endpoint: the plan-reorder PATCH
  // `/project_stages/{id}/locations/` (issue #816). The queue's `meta.type` is the
  // FIRST path segment, so that URL queues as `project_stages` even though every
  // other location write queues as `locations` — and without this entry a reorder
  // would sync silently and never invalidate the list it just changed.
  //
  // The cost is an extra locations refetch when some OTHER queued write happens to
  // start with `/project_stages` — today only the stage-scoped issue POST. One
  // refetch after a queue burst is cheaper than keying this off a URL the queue
  // does not store, and far cheaper than a reorder that does not stick.
  project_stages: ['stageLocations', 'getProjectStageLocations'],
}

// Wait a beat after the queue empties before invalidating. The server has
// just finished processing the last item; Mercure broadcasts may still be in
// flight. Letting them settle first means our refetch reads from a fully
// applied state instead of racing.
const POST_DRAIN_GRACE_MS = 300

export function useQueueDrainInvalidator(): void {
  useEffect(() => {
    const types = Object.keys(DRAIN_INVALIDATION_MAP)

    // Last observed count per type. Updated on every store tick — used to
    // detect the >0 → 0 transition that marks the end of a sync burst.
    const prevCount: Record<string, number> = {}
    const seed = useSyncQueueStore.getState().data
    for (const type of types) prevCount[type] = countPendingQueueEntriesForType(type, seed)

    // `needsInvalidate[type]` is set when we cross >0 → 0 but can't fire yet
    // (bulk loop still in flight, or simply waiting out the grace window).
    // Both the queue and bulk-flag listeners try to flush it.
    const needsInvalidate: Record<string, boolean> = {}
    for (const type of types) needsInvalidate[type] = false

    const pendingTimers: Record<string, ReturnType<typeof setTimeout> | null> = {}

    const fire = (type: string) => {
      const keys = DRAIN_INVALIDATION_MAP[type]
      if (!keys) return
      for (const key of keys) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      needsInvalidate[type] = false
    }

    const tryFlush = (type: string) => {
      if (!needsInvalidate[type]) return
      // Bulk loop still adding items → defer; we'll be retried when it ends.
      if (useBulkSelectStore.getState().isBulkInFlight) return
      // Queue refilled while we were waiting → no drain to mark.
      if (countPendingQueueEntriesForType(type, useSyncQueueStore.getState().data) > 0) return

      if (pendingTimers[type]) clearTimeout(pendingTimers[type]!)
      pendingTimers[type] = setTimeout(() => {
        pendingTimers[type] = null
        // Final guard — situation may have changed across the grace window.
        if (useBulkSelectStore.getState().isBulkInFlight) return
        if (countPendingQueueEntriesForType(type, useSyncQueueStore.getState().data) > 0) return
        fire(type)
      }, POST_DRAIN_GRACE_MS)
    }

    const unsubQueue = useSyncQueueStore.subscribe(state => {
      for (const type of types) {
        const next = countPendingQueueEntriesForType(type, state.data)
        const wasPending = prevCount[type] > 0
        prevCount[type] = next

        if (next > 0 && pendingTimers[type]) {
          // Work resumed before the grace window expired — cancel.
          clearTimeout(pendingTimers[type]!)
          pendingTimers[type] = null
        }

        if (wasPending && next === 0) {
          needsInvalidate[type] = true
          tryFlush(type)
        }
      }
    })

    // Selector-style subscribe: we only care about `isBulkInFlight`
    // transitions, not every selection toggle / anchor / mode change the
    // store emits. Manual comparison so we don't need to add the
    // subscribeWithSelector middleware to a store that mostly doesn't
    // need it.
    let prevBulkFlag = useBulkSelectStore.getState().isBulkInFlight
    const unsubBulk = useBulkSelectStore.subscribe(state => {
      if (state.isBulkInFlight === prevBulkFlag) return
      prevBulkFlag = state.isBulkInFlight
      // We only care about the `true → false` transition: the bulk loop
      // just released its in-flight gate, and if the queue already
      // drained while it was running, that drain has been waiting for us.
      if (state.isBulkInFlight) return
      for (const type of types) tryFlush(type)
    })

    return () => {
      unsubQueue()
      unsubBulk()
      for (const t of Object.keys(pendingTimers)) {
        const timer = pendingTimers[t]
        if (timer) clearTimeout(timer)
      }
    }
  }, [])
}
